import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Package } from 'lucide-react';
import { cn } from '@/utils';
import { SJSkeleton } from '@/components/ui/sj-skeleton';

interface MegaMenuItem {
  slug: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  logoUrl?: string | null;
  color?: string | null;
}

interface SJMegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  items: MegaMenuItem[];
  isLoading: boolean;
  basePath: string;
  viewAllLabel: string;
  columns?: 2 | 3 | 4;
}

export function SJMegaMenu({
  isOpen,
  onClose,
  items,
  isLoading,
  basePath,
  viewAllLabel,
  columns = 3,
}: SJMegaMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const gridCols = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 lg:grid-cols-4',
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={menuRef}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="absolute left-1/2 top-full mt-2 w-[640px] -translate-x-1/2 rounded-[16px] border border-[var(--sj-border)] bg-[var(--sj-surface)] p-4 shadow-xl"
        >
          {isLoading ? (
            <div className={cn('grid gap-3', gridCols[columns])}>
              {Array.from({ length: 6 }).map((_, i) => (
                <SJSkeleton key={i} className="h-16 rounded-[10px]" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="py-6 text-center text-sm text-[var(--sj-text-muted)]">
              Content coming soon
            </p>
          ) : (
            <>
              <div className={cn('grid gap-1', gridCols[columns])}>
                {items.map((item) => {
                  const accentColor = item.color ?? '#de4c49';
                  const showIcon = Boolean(item.logoUrl || item.icon || item.color);

                  return (
                    <Link
                      key={item.slug}
                      to={`${basePath}/${item.slug}`}
                      onClick={onClose}
                      className="flex items-start gap-3 rounded-[10px] px-3 py-2.5 transition-colors hover:bg-[var(--sj-surface-muted)]"
                    >
                      {showIcon && (
                        <div
                          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-[var(--sj-border)] bg-[var(--sj-surface)]"
                          style={{ backgroundColor: item.logoUrl ? undefined : `${accentColor}15` }}
                        >
                          {item.logoUrl ? (
                            <img
                              src={item.logoUrl}
                              alt=""
                              className="h-full w-full object-contain p-1.5"
                            />
                          ) : item.icon ? (
                            <span className="text-base leading-none">{item.icon}</span>
                          ) : (
                            <Package className="h-4 w-4" style={{ color: accentColor }} />
                          )}
                        </div>
                      )}
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-[var(--sj-text-primary)]">
                          {item.name}
                        </span>
                        {item.description && (
                          <span className="mt-0.5 block text-xs text-[var(--sj-text-muted)] line-clamp-1">
                            {item.description}
                          </span>
                        )}
                      </span>
                    </Link>
                  );
                })}
              </div>
              <div className="mt-3 border-t border-[var(--sj-border)] pt-3">
                <Link
                  to={basePath}
                  onClick={onClose}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  {viewAllLabel}
                </Link>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
